"use client";

import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { login as loginApi, setTokens } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { getSafePostLoginRedirect } from "@/lib/utils/auth-helpers";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";
import LoginForm, { type LoginFormData } from "@/components/auth/LoginForm";
import type { User } from "@/lib/types";

export default function LoginPage() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const finalizeAuth = (user: User) => {
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    window.location.href = getSafePostLoginRedirect(user, redirect);
  };

  const handleLogin = async (data: LoginFormData) => {
    setApiError(null);
    setIsSubmitLoading(true);

    try {
      const response = await loginApi({
        email: data.email,
        password: data.password,
      });
      const { user, accessToken, refreshToken } = response;

      setTokens(accessToken, refreshToken);
      setAuth(user, accessToken);

      finalizeAuth(user);
    } catch (err) {
      setApiError(getErrorMessage(err));
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleSocialLoginSuccess = async (data: unknown) => {
    const result = data as {
      accessToken?: string;
      refreshToken?: string;
      user?: User | null;
    };

    if (result.accessToken && result.refreshToken && result.user) {
      setTokens(result.accessToken, result.refreshToken);
      setAuth(result.user, result.accessToken);

      finalizeAuth(result.user);
    }
  };

  const getErrorMessage = (err: unknown): string => {
    const msg = String((err as { message?: string })?.message ?? "");
    const code = (err as { code?: string })?.code ?? "";
    if (code === "EMAIL_NOT_VERIFIED" || msg.toLowerCase().includes("verified")) {
      return "Please verify your email. Check your inbox for the verification link.";
    }
    if (code === "ACCOUNT_SUSPENDED" || msg.toLowerCase().includes("suspended")) {
      return "Your account has been suspended. Contact support for assistance.";
    }
    if (
      msg.toLowerCase().includes("credential") ||
      msg.toLowerCase().includes("password") ||
      msg.toLowerCase().includes("invalid")
    ) {
      return "The email or password is not correct. Please check both and try again.";
    }
    return msg || "We could not sign you in. Please try again.";
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <p className="text-xs font-medium uppercase tracking-widest text-neon-cyan">
          Sign in securely
        </p>
        <h1 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl">
          Welcome back
        </h1>
        <p className="mt-2 text-sm text-ash">
          Sign in to continue managing your maritime career.
        </p>
      </div>

      {apiError && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex items-start gap-3 rounded-lg border border-danger/20 bg-danger/5 p-3 text-sm text-danger"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{apiError}</span>
        </div>
      )}

      <SocialAuthButtons
        mode="login"
        onSuccess={handleSocialLoginSuccess}
        onError={(msg) => setApiError(msg)}
      />

      <div className="pt-2">
        <LoginForm onSubmit={handleLogin} loading={isSubmitLoading} />
      </div>

      <div className="rounded-lg border border-border/50 bg-muted/10 p-3 text-center">
        <p className="text-xs leading-relaxed text-ash">
          Your information is protected using secure authentication practices.
        </p>
      </div>
    </div>
  );
}
